/**
 * ChangelogHome — Public changelog timeline (changelog.example.com)
 * Design: vertical timeline, dark theme, tag badges
 */

import { T } from '../styles/tokens';
import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { Button, Spinner } from '../components/ui';

const API_BASE = '/api/v1/cms';

interface Entry {
  id: string; slug: string; tags: string; metadata: string; author_name: string;
  published_at: string; created_at: string; title: string; excerpt: string;
  version?: string;
}

const TAG_COLORS: Record<string, string> = {
  feature: T.success, fix: T.danger, improvement: T.primary, breaking: T.warning,
};

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" } as any,
  container: { maxWidth: 800, margin: '0 auto', padding: '40px 20px' } as any,
  header: { marginBottom: 48, textAlign: 'center' as const },
  title: { fontSize: 32, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.02em' },
  subtitle: { fontSize: 15, color: T.textMuted },
  timeline: { position: 'relative' as const, paddingLeft: 32 } as any,
  timelineLine: { position: 'absolute' as const, left: 7, top: 0, bottom: 0, width: 2, background: `linear-gradient(180deg, ${T.primary} 0%, ${T.primaryBg} 100%)` } as any,
  dot: { position: 'absolute' as const, left: 0, top: 8, width: 16, height: 16, borderRadius: '50%', background: T.bg, border: `3px solid ${T.primary}`, zIndex: 1 } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px 24px', marginBottom: 24, position: 'relative' as const, cursor: 'pointer', transition: 'border-color 0.15s' } as any,
  date: { fontSize: 13, color: T.textMuted, marginBottom: 6 },
  entryTitle: { fontSize: 18, fontWeight: 600, marginBottom: 8, lineHeight: 1.3 },
  excerpt: { fontSize: 14, color: T.textSecondary, lineHeight: 1.6, marginBottom: 12 },
  tags: { display: 'flex', gap: 6, flexWrap: 'wrap' as const },
  tag: (color: string) => ({ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: `${color}20`, color }),
  version: { fontSize: 12, color: T.primary, fontWeight: 500, marginLeft: 8 },
  skeleton: { background: T.bgCardHover, borderRadius: 8, height: 120, marginBottom: 24 } as any,
  loadMore: { display: 'block', width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, background: T.bgCardHover, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textSecondary, cursor: 'pointer', textAlign: 'center' as const, marginTop: 16 },
  rssLink: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.textMuted, textDecoration: 'none', marginTop: 8 },
};

function formatDate(d: string): string {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return d; }
}

function parseTags(tags: string): string[] {
  try { return JSON.parse(tags || '[]'); } catch { return []; }
}

export function ChangelogHomePage(_props: { path?: string; locale?: string }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const locale = _props.locale || 'pt-BR';
  const LIMIT = 20;

  const load = (off: number, append = false) => {
    setLoading(true);
    fetch(`${API_BASE}/entries?contentType=changelog&status=published&locale=${encodeURIComponent(locale)}&limit=${LIMIT}&offset=${off}`)
      .then(r => r.json())
      .then((res: any) => {
        const items = res?.data?.entries || [];
        setEntries(prev => append ? [...prev, ...items] : items);
        setTotal(res?.data?.total || res?.meta?.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(0); }, [locale]);

  const loadMore = () => {
    const newOff = offset + LIMIT;
    setOffset(newOff);
    load(newOff, true);
  };

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.header}>
          <h1 style={S.title}>Changelog</h1>
          <p style={S.subtitle}>Novidades e atualizacoes da plataforma WhiteLabelClaw</p>
          <a href={`/feed.xml?locale=${locale}`} style={S.rssLink}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 11a9 9 0 019 9"/><path d="M4 4a16 16 0 0116 16"/><circle cx="5" cy="19" r="1"/></svg>
            RSS Feed
          </a>
        </div>

        <div style={S.timeline}>
          <div style={S.timelineLine} />

          {loading && entries.length === 0 && (
            <>
              <div style={S.skeleton} />
              <div style={S.skeleton} />
              <div style={S.skeleton} />
            </>
          )}

          {entries.map((entry) => {
            const tags = parseTags(entry.tags);
            return (
              <div
                key={entry.id}
                style={S.card}
                onClick={() => route(`/${locale}/${entry.slug}`)}
                onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.bgActive; }}
                onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; }}
              >
                <div style={S.dot} />
                <div style={S.date}>
                  {formatDate(entry.published_at || entry.created_at)}
                  {(() => { try { const v = JSON.parse(entry.metadata || '{}').version; return v ? <span style={S.version}>v{v}</span> : null; } catch { return null; } })()}
                </div>
                <div style={S.entryTitle}>{entry.title || entry.slug}</div>
                {entry.excerpt && <div style={S.excerpt}>{entry.excerpt}</div>}
                <div style={S.tags}>
                  {tags.map((tag) => (
                    <span key={tag} style={S.tag(TAG_COLORS[tag] || T.textMuted)}>{tag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {entries.length < total && (
          <Button variant="ghost" onClick={loadMore} disabled={loading} fullWidth style={S.loadMore}>
            {loading ? <><Spinner size="sm" inline /> Carregando...</> : 'Carregar mais'}
          </Button>
        )}
      </div>
    </div>
  );
}
